import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_URL from '../api';
import TalentPool from '../components/TalentPool';
import { ActionButton } from '../ui';
import logoMark from '../assets/images/logo.jpg';
import './FindStudents.css';

// Dashboard-level, on-demand talent discovery. Deliberately standalone: a
// recruiter can source candidates from a JD without ever posting a job.
// Feeds the same pipeline as the automatic per-job pool (POST /api/talent/search).

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const MODES = [
  { key: 'paste', label: 'Paste description' },
  { key: 'upload', label: 'Upload a JD' },
  { key: 'job', label: 'Use one of my jobs' },
];

const MAX_FILE_MB = 5;

function friendlyError(err) {
  const status = err?.response?.status;
  const msg = err?.response?.data?.error || '';
  if (status === 422 || /too short|could not|unsupported|no text/i.test(msg)) {
    return msg || 'That job description could not be read. Try pasting the text instead.';
  }
  if (!err?.response || status >= 502) {
    return 'The matching service is unavailable right now. Please try again in a few minutes.';
  }
  return msg || 'Something went wrong starting the search.';
}

export default function FindStudents({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('paste');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchId, setSearchId] = useState(null);

  const fileInputRef = useRef(null);

  // Recruiter's own jobs, for the "use an existing job" path.
  useEffect(() => {
    axios.get(`${API_URL}/api/jobs`, { headers: authHeaders() })
      .then((res) => setJobs(res.data || []))
      .catch(() => { /* the dropdown just stays empty */ });
  }, []);

  // Deep link from the dashboard: ?job=<id> preselects that job.
  useEffect(() => {
    const preselected = searchParams.get('job');
    if (preselected) {
      setMode('job');
      setJobId(preselected);
    }
  }, [searchParams]);

  const handleFile = (selected) => {
    setError(null);
    if (!selected) { setFile(null); return; }
    if (!/\.(pdf|docx|txt)$/i.test(selected.name)) {
      setError('Upload a PDF, DOCX, or TXT job description.');
      setFile(null);
      return;
    }
    if (selected.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`That file is larger than ${MAX_FILE_MB} MB.`);
      setFile(null);
      return;
    }
    setFile(selected);
  };

  const canSubmit = () => {
    if (submitting) return false;
    if (mode === 'paste') return description.trim().length >= 40;
    if (mode === 'upload') return !!file;
    return !!jobId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setSubmitting(true);
    setError(null);
    setSearchId(null);

    try {
      let res;
      if (mode === 'upload') {
        const form = new FormData();
        form.append('file', file);
        if (title.trim()) form.append('title', title.trim());
        res = await axios.post(`${API_URL}/api/talent/search`, form, { headers: authHeaders() });
      } else if (mode === 'job') {
        res = await axios.post(`${API_URL}/api/talent/search`, { job_id: jobId }, { headers: authHeaders() });
      } else {
        res = await axios.post(
          `${API_URL}/api/talent/search`,
          { title: title.trim(), description },
          { headers: authHeaders() }
        );
      }
      setSearchId(res.data.search_id);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setSearchId(null);
    setError(null);
  };

  return (
    <div className="fs-page">
      <nav className="fs-nav">
        <button className="fs-brand" onClick={() => navigate('/recruiter/dashboard')}>
          <img src={logoMark} alt="" className="brand-mark" />
          <div>
            <div className="fs-brand-title">HYRSKILL</div>
            <div className="fs-brand-sub">Recruiter Portal</div>
          </div>
        </button>
        <div className="fs-nav-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/dashboard')}>
            ← Dashboard
          </button>
          {onLogout && (
            <button className="btn btn-secondary btn-sm" onClick={onLogout}>Sign Out</button>
          )}
        </div>
      </nav>

      <div className="fs-body ui-view">
        <header className="fs-header">
          <h1>Find Relevant Students</h1>
          <p>
            Describe the role and we’ll surface verified students who fit — no job
            posting required, and no waiting for applications.
          </p>
        </header>

        {!searchId && (
          <form className="fs-card" onSubmit={handleSubmit}>
            <div className="fs-modes" role="tablist" aria-label="Job description source">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === m.key}
                  className={`fs-mode${mode === m.key ? ' is-active' : ''}`}
                  onClick={() => { setMode(m.key); setError(null); }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {mode !== 'job' && (
              <label className="fs-field">
                <span>Job title <em>(optional)</em></span>
                <input
                  type="text"
                  value={title}
                  maxLength={120}
                  placeholder="e.g. Backend Engineering Intern"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
            )}

            {mode === 'paste' && (
              <label className="fs-field">
                <span>Job description</span>
                <textarea
                  rows={12}
                  value={description}
                  placeholder="Paste the full job description here — responsibilities, required skills, experience, location…"
                  onChange={(e) => setDescription(e.target.value)}
                />
                <small className={description.trim().length > 0 && description.trim().length < 40 ? 'is-warning' : ''}>
                  {description.trim().length < 40
                    ? `At least 40 characters needed (${description.trim().length} so far).`
                    : `${description.trim().length} characters.`}
                </small>
              </label>
            )}

            {mode === 'upload' && (
              <div className="fs-field">
                <span>Job description file</span>
                <div
                  className={`fs-drop${file ? ' has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFile(e.dataTransfer.files?.[0]);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                >
                  {file ? (
                    <>
                      <strong>{file.name}</strong>
                      <span>{(file.size / 1024).toFixed(0)} KB · click to replace</span>
                    </>
                  ) : (
                    <>
                      <strong>Drop a JD here, or click to browse</strong>
                      <span>PDF, DOCX or TXT · up to {MAX_FILE_MB} MB</span>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  hidden
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
            )}

            {mode === 'job' && (
              <label className="fs-field">
                <span>Pick a job</span>
                <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                  <option value="">Select one of your jobs…</option>
                  {jobs.map((job) => (
                    <option key={job._id} value={job._id}>
                      {job.title} — {job.position_type} · {job.status}
                    </option>
                  ))}
                </select>
                {jobs.length === 0 && (
                  <small>You haven’t posted any jobs yet. Paste a description instead.</small>
                )}
              </label>
            )}

            {error && <div className="fs-alert" role="alert">{error}</div>}

            {/* One control, three states — it never swaps out under the cursor. */}
            <ActionButton
              type="submit"
              size="md"
              className="fs-submit"
              state={submitting ? 'pending' : 'idle'}
              labels={{ idle: 'Find Students', pending: 'Searching…', done: 'Done' }}
              disabled={!canSubmit()}
            />
          </form>
        )}

        {searchId && (
          <>
            <div className="fs-result-head">
              <button className="btn btn-secondary btn-sm" onClick={startOver}>
                ← New search
              </button>
            </div>
            <TalentPool searchId={searchId} />
          </>
        )}
      </div>
    </div>
  );
}
